import { useMemo, useState } from "react";
import { AlertTriangle, CalendarDays, ChevronLeft, ChevronRight, FileText, User } from "lucide-react";
import { ErrorAlert, LoadingSpinner } from "../../components/ui";
import type { EventoAgenda } from "../../types/agenda";
import { useAgenda } from "./api";

const hoje = new Date();
const doisDigitos = (n: number) => String(n).padStart(2, "0");

const MESES = ["", "Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const AgendaPage = () => {
  const [ano, setAno] = useState(hoje.getFullYear());
  const [mes, setMes] = useState(hoje.getMonth() + 1);

  // Janela semiaberta: do primeiro dia do mês ao primeiro dia do mês seguinte.
  const [inicio, fim] = useMemo(() => {
    const proximoMes = mes === 12 ? 1 : mes + 1;
    const anoDoProximo = mes === 12 ? ano + 1 : ano;
    return [
      `${ano}-${doisDigitos(mes)}-01`,
      `${anoDoProximo}-${doisDigitos(proximoMes)}-01`,
    ];
  }, [ano, mes]);

  const { data, isLoading, isError, error, refetch } = useAgenda(inicio, fim);

  const negado =
    (error as { response?: { status?: number } })?.response?.status === 403;

  const porDia = useMemo(() => {
    const mapa = new Map<string, EventoAgenda[]>();
    for (const e of data?.eventos ?? []) {
      const dia = e.inicio.slice(0, 10);
      const lista = mapa.get(dia) ?? [];
      lista.push(e);
      mapa.set(dia, lista);
    }
    return [...mapa.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [data]);

  const navegar = (passo: number) => {
    const novo = mes + passo;
    if (novo < 1) { setMes(12); setAno(ano - 1); }
    else if (novo > 12) { setMes(1); setAno(ano + 1); }
    else setMes(novo);
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-800 via-blue-600 to-blue-500 bg-clip-text text-transparent">
          Agenda
        </h1>
        <p className="text-gray-600 mt-1">
          Atendimentos agendados e status reports do seu cliente.
        </p>
      </div>

      <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-4 flex items-center gap-4">
        <button
          type="button"
          onClick={() => navegar(-1)}
          aria-label="Mês anterior"
          className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span className="text-lg font-semibold text-gray-800 min-w-[12rem] text-center">
          {MESES[mes]} de {ano}
        </span>
        <button
          type="button"
          onClick={() => navegar(1)}
          aria-label="Próximo mês"
          className="rounded-lg border border-gray-200 p-2 text-gray-600 hover:bg-gray-50"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {isLoading && (
        <div className="flex justify-center py-16"><LoadingSpinner /></div>
      )}

      {negado && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-12 text-center">
          <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-700 font-medium">
            A agenda não está habilitada para o seu acesso.
          </p>
          <p className="text-gray-500 text-sm mt-1">
            Fale com o time da T4i se precisar acompanhar os agendamentos por aqui.
          </p>
        </div>
      )}

      {isError && !negado && (
        <ErrorAlert
          title="Não foi possível carregar a agenda"
          message={(error as Error)?.message ?? "Tente novamente."}
          onRetry={() => refetch()}
          variant="error"
        />
      )}

      {data && data.statusReportsDisponiveis === false && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
          <span>
            Os atendimentos estão listados, mas os status reports não puderam ser
            carregados agora.
          </span>
        </p>
      )}

      {data && porDia.length === 0 && (
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 p-12 text-center">
          <p className="text-gray-600 font-medium">
            Nada agendado em {MESES[mes].toLowerCase()} de {ano}.
          </p>
          <p className="text-gray-500 text-sm mt-1">Use as setas para ver outro mês.</p>
        </div>
      )}

      {porDia.map(([dia, eventos]) => (
        <section
          key={dia}
          className="bg-white/70 backdrop-blur-sm rounded-2xl border border-gray-200/60 overflow-hidden"
        >
          <h2 className="px-5 py-3 bg-gray-50/80 text-sm font-semibold text-gray-700 border-b border-gray-200/60">
            {dia.split("-").reverse().join("/")}
          </h2>
          <ul className="divide-y divide-gray-100">
            {eventos.map((e, i) => (
              <li key={`${dia}-${i}`} className="px-5 py-3 flex items-start gap-3">
                <span
                  className="mt-1.5 w-2.5 h-2.5 rounded-full shrink-0"
                  style={{ backgroundColor: e.cor }}
                  aria-hidden
                />
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-800">{e.titulo}</p>
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500 mt-0.5">
                    <span>
                      {e.diaInteiro
                        ? "Dia inteiro"
                        : `${e.inicio.slice(11, 16)} às ${e.fim.slice(11, 16)}`}
                    </span>
                    {e.responsavel && (
                      <span className="inline-flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {e.responsavel}
                      </span>
                    )}
                    {e.tipoAtendimento && <span>{e.tipoAtendimento}</span>}
                    {e.tipo === "statusReport" && (
                      <span
                        className="inline-flex items-center gap-1 text-gray-400"
                        title="O download do relatório ainda não está disponível"
                      >
                        <FileText className="w-3 h-3" />
                        relatório indisponível
                      </span>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
};

export default AgendaPage;
